import { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { BsEnvelopePaper, BsXLg } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Select from '#globalComponents/Forms/Select';
import TextInput from '#globalComponents/Forms/TextInput';
import IconButton from '#globalComponents/IconButton/IconButton';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import { useErrorStore } from '#lib/stores/errorStore';
import { isAutoSendable, isDuplexDesign, letterPriceLabel } from '#postMailingFeature/constants/pmPricing';
import { usePostMailingTemplates } from '#postMailingFeature/models/postMailingTemplates/queries';

import { useCreateActivity, useUpdateActivity } from '../../../../models/activities/mutations';

import { ScheduleModeToggle, nowLocal, resolveDueAt } from './shared';

const buildLetterPayload = ({
	isEdit,
	sendMode,
	isImmediate,
	title,
	dueAt,
	selectedTemplateId,
	selectedContactId,
	postTemplates,
	companyId,
	dealId,
	channelTypeId,
	initialData,
}) => {
	const resolvedTitle =
		sendMode === 'template'
			? (postTemplates.find((t) => t.id === selectedTemplateId)?.name ?? 'Brief versenden')
			: title.trim();
	const resolvedDueAt = resolveDueAt({ isImmediate, dueAt });

	if (isEdit) {
		const payload = { title: resolvedTitle, dueAt: resolvedDueAt, crmContactId: selectedContactId || null };
		if (sendMode === 'template') payload.postMailingTemplateId = selectedTemplateId;
		return { id: initialData.id, payload };
	}

	const base = {
		crmCompanyId: companyId,
		...(dealId && { dealId }),
		channelTypeId: channelTypeId ?? null,
	};

	if (sendMode === 'template') {
		return {
			...base,
			activityType: 'communication',
			title: resolvedTitle,
			status: 'planned',
			dueAt: resolvedDueAt,
			postMailingTemplateId: selectedTemplateId,
			crmContactId: selectedContactId || null,
		};
	}

	return { ...base, activityType: 'task', title: resolvedTitle, status: 'planned', dueAt: resolvedDueAt };
};

/**
 * Resolves the submit button label for the current mode.
 * @param {{isEdit: boolean, sendMode: string, isImmediate: boolean}} state
 * @returns {string}
 */
const submitLabel = ({ isEdit, sendMode, isImmediate }) => {
	if (isEdit) return 'Speichern';
	if (sendMode !== 'template') return 'To-Do anlegen';
	return isImmediate ? 'Jetzt senden' : 'Planen';
};

/**
 * Determines whether the submit button is disabled for the current form state.
 * @param {object} state
 * @returns {boolean}
 */
const isSubmitDisabled = ({
	sendMode,
	title,
	selectedTemplateId,
	selectedContactId,
	isCompleted,
	isImmediate,
	dueAt,
	templateBlocked,
}) => {
	if (sendMode !== 'template') return !title.trim();
	if (!selectedTemplateId || !selectedContactId || templateBlocked) return true;
	return !isCompleted && !isImmediate && !dueAt;
};

const LetterTemplateFields = ({
	selectedTemplateId,
	setSelectedTemplateId,
	selectedContactId,
	setSelectedContactId,
	postTemplates,
	contacts,
}) => (
	<>
		<Select value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)}>
			<option value="">Template wählen…</option>
			{postTemplates.map((tpl) => (
				<option key={tpl.id} value={tpl.id}>
					{tpl.name}
				</option>
			))}
		</Select>
		{postTemplates.length === 0 && (
			<p className="text-[11px] text-bgBase-800">
				Noch keine Templates.{' '}
				<a href="/post-mailing/templates" className="text-primary-600">
					Jetzt erstellen →
				</a>
			</p>
		)}
		{contacts.length > 0 ? (
			<Select value={selectedContactId} onChange={(e) => setSelectedContactId(e.target.value)}>
				<option value="">Empfänger wählen…</option>
				{contacts.map((c) => (
					<option key={c.id} value={c.id}>
						{c.fullName ??
							(`${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || c.emailAddress || 'Unbenannt')}
					</option>
				))}
			</Select>
		) : (
			<p className="text-[11px] text-caution-700">Kein Kontakt vorhanden. Bitte zuerst einen Kontakt anlegen.</p>
		)}
	</>
);

LetterTemplateFields.propTypes = {
	selectedTemplateId: PropTypes.string.isRequired,
	setSelectedTemplateId: PropTypes.func.isRequired,
	selectedContactId: PropTypes.string.isRequired,
	setSelectedContactId: PropTypes.func.isRequired,
	postTemplates: PropTypes.array.isRequired,
	contacts: PropTypes.array.isRequired,
};

const LetterForm = ({ companyId, dealId, contacts = [], onClose, initialData, channelTypeId }) => {
	const isEdit = Boolean(initialData);
	const [sendMode, setSendMode] = useState(() =>
		isEdit ? (initialData.postMailingTemplateId ? 'template' : 'manual') : 'template',
	);
	const [title, setTitle] = useState(initialData?.title ?? '');
	// 'now' posts the letter immediately; 'later' schedules it for the chosen dueAt.
	// Only relevant when creating a template send.
	const [scheduleMode, setScheduleMode] = useState('now');
	const [dueAt, setDueAt] = useState(
		initialData?.dueAt ? new Date(initialData.dueAt).toISOString().slice(0, 16) : '',
	);
	const [selectedTemplateId, setSelectedTemplateId] = useState(initialData?.postMailingTemplateId ?? '');
	const [selectedContactId, setSelectedContactId] = useState(initialData?.crmContactId ?? '');
	const [confirmOpen, setConfirmOpen] = useState(false);
	const createActivity = useCreateActivity();
	const updateActivity = useUpdateActivity();
	const { data: postTemplates = [] } = usePostMailingTemplates();
	const showError = useErrorStore((s) => s.showError);

	useEffect(() => {
		if (selectedContactId || contacts.length === 0) return;
		const primary = contacts.find((c) => c.isPrimary);
		setSelectedContactId(primary?.id ?? (contacts.length === 1 ? contacts[0].id : ''));
	}, [contacts, selectedContactId]);

	const isPending = isEdit ? updateActivity.isPending : createActivity.isPending;
	const isCompleted = initialData?.status === 'completed';
	const isImmediate = !isEdit && sendMode === 'template' && scheduleMode === 'now';

	const selectedContact = contacts.find((c) => c.id === selectedContactId);
	const recipientName =
		selectedContact?.fullName ||
		`${selectedContact?.firstName ?? ''} ${selectedContact?.lastName ?? ''}`.trim() ||
		'diesen Kontakt';

	const selectedDesign = postTemplates.find((t) => t.id === selectedTemplateId)?.designData;
	// Sonderformat PDFs are offer-only — the backend rejects them, so block the send here too.
	const templateBlocked = Boolean(selectedTemplateId) && !isAutoSendable(selectedDesign);
	const priceLabel = letterPriceLabel(selectedDesign);
	const duplex = isDuplexDesign(selectedDesign);

	const submit = async () => {
		try {
			const built = buildLetterPayload({
				isEdit,
				sendMode,
				isImmediate,
				title,
				dueAt,
				selectedTemplateId,
				selectedContactId,
				postTemplates,
				companyId,
				dealId,
				channelTypeId,
				initialData,
			});
			if (isEdit) {
				await updateActivity.mutateAsync(built);
			} else {
				await createActivity.mutateAsync(built);
			}
			onClose();
		} catch (err) {
			showError({ error: err, title: isEdit ? 'Speichern fehlgeschlagen' : 'Fehler beim Speichern' });
			// Rethrow so the confirm dialog stays open on a failed send instead of closing over the error.
			throw err;
		}
	};

	const handleSubmit = () => {
		if (sendMode === 'template' && (!selectedTemplateId || templateBlocked)) return;
		if (sendMode === 'manual' && !title.trim()) return;

		// An immediate letter is irreversible and billable — confirm before it leaves.
		if (isImmediate) {
			setConfirmOpen(true);
			return;
		}
		submit().catch(() => {});
	};

	return (
		<div className="p-3 space-y-2.5">
			<div className="flex items-center gap-2">
				<div className="flex items-center gap-1.5 px-2 py-1 rounded-control bg-primary-100 text-primary-600 text-xs font-medium">
					<BsEnvelopePaper className="w-4 h-4" /> Brief
				</div>
				<IconButton icon={BsXLg} label="Schließen" onClick={onClose} className="ml-auto h-6 w-6" />
			</div>

			{!isEdit && (
				<div className="flex gap-1.5">
					<button
						type="button"
						onClick={() => setSendMode('template')}
						className={`flex-1 text-xs px-2.5 py-2 rounded-control border transition-colors ${sendMode === 'template' ? 'border-primary-300 bg-primary-100 text-primary-700 font-medium' : 'border-bgBase-500 text-bgBase-800 hover:border-bgBase-600'}`}
					>
						Mit Template versenden
					</button>
					<button
						type="button"
						onClick={() => setSendMode('manual')}
						className={`flex-1 text-xs px-2.5 py-2 rounded-control border transition-colors ${sendMode === 'manual' ? 'border-primary-300 bg-primary-100 text-primary-700 font-medium' : 'border-bgBase-500 text-bgBase-800 hover:border-bgBase-600'}`}
					>
						Manuell erledigen
					</button>
				</div>
			)}

			{sendMode === 'template' ? (
				<LetterTemplateFields
					selectedTemplateId={selectedTemplateId}
					setSelectedTemplateId={setSelectedTemplateId}
					selectedContactId={selectedContactId}
					setSelectedContactId={setSelectedContactId}
					postTemplates={postTemplates}
					contacts={contacts}
				/>
			) : (
				<TextInput
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					placeholder="z.B. Angebot per Post senden"
					autoFocus
				/>
			)}

			{sendMode === 'template' && templateBlocked && (
				<p className="text-[11px] text-caution-700">
					Sonderformat-Vorlagen können nicht automatisch versendet werden. Bitte nutzen Sie dafür eine
					Post-Mailing-Kampagne mit Angebotsanfrage.
				</p>
			)}

			{!isEdit && sendMode === 'template' && !templateBlocked && (
				<ScheduleModeToggle value={scheduleMode} onChange={setScheduleMode} />
			)}

			{sendMode === 'template' && selectedTemplateId && !templateBlocked && (
				<p className="text-[10px] text-bgBase-800">
					Kosten: {priceLabel} zzgl. MwSt.{duplex && ' · beidseitiger Druck'}
				</p>
			)}

			{!isCompleted && !isImmediate && (
				<div>
					<TextInput
						type="datetime-local"
						value={dueAt}
						onChange={(e) => setDueAt(e.target.value)}
						min={nowLocal()}
					/>
					{sendMode === 'template' && (
						<p className="text-[10px] text-bgBase-800 mt-0.5">Zeitpunkt für automatischen Versand</p>
					)}
				</div>
			)}

			<div className="flex justify-end gap-2 pt-1">
				<Button type="button" variant="outline" size="sm" onClick={onClose}>
					Abbrechen
				</Button>
				<Button
					variant="solid"
					size="sm"
					onClick={handleSubmit}
					loading={isPending}
					disabled={isSubmitDisabled({
						sendMode,
						title,
						selectedTemplateId,
						selectedContactId,
						isCompleted,
						isImmediate,
						dueAt,
						templateBlocked,
					})}
				>
					{submitLabel({ isEdit, sendMode, isImmediate })}
				</Button>
			</div>

			<ConfirmModal
				isOpen={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				onConfirm={submit}
				title="Brief kostenpflichtig versenden?"
				message={
					<>
						Der Brief an <strong>{recipientName}</strong> wird sofort gedruckt und versendet.
						<span className="block mt-2">
							Kosten: <strong>{priceLabel}</strong> zzgl. MwSt.
							{duplex && <span className="text-bgBase-800"> (beidseitiger Druck)</span>}
						</span>
					</>
				}
				subtitle="Diese Aktion kann nicht rückgängig gemacht werden. Die Kosten werden mit Ihrer nächsten monatlichen Rechnung abgerechnet."
				confirmText={`Kostenpflichtig senden (${priceLabel})`}
				color="primary"
				icon={BsEnvelopePaper}
				isLoading={isPending}
			/>
		</div>
	);
};
LetterForm.propTypes = {
	companyId: PropTypes.string,
	dealId: PropTypes.string,
	contacts: PropTypes.array,
	onClose: PropTypes.func,
	initialData: PropTypes.object,
	channelTypeId: PropTypes.string,
};

export default LetterForm;
